import { BREAKPOINT_MEDIA_QUERIES } from '../../utils/breakpoints';
import { createElement, defineModule, nextTick } from '../../utils/helpers';

let hasDoneFirstMove = false;
let underlineElement: HTMLElement | null = null;

const getElements = (menuItemElement?: HTMLElement) => {
  const navbarElement = document.querySelector<HTMLElement>('.navbar');
  const navbarNavElement = document.querySelector<HTMLButtonElement>(
    '.navbar .navbar__nav',
  );

  const link = menuItemElement?.querySelector<HTMLElement>('a') ?? null;
  const icon =
    link?.querySelector<HTMLElement>('.menu-item__dropdown-icon') ?? null;

  return {
    navbarElement,
    navbarNavElement,
    link,
    icon,
  };
};

const getActiveMenuItem = () => {
  const { navbarNavElement } = getElements();
  if (!navbarNavElement) return;

  const activeMenuItem = [...navbarNavElement.children]
    .filter(
      (child) =>
        child.classList.contains('current-menu-item') ||
        child.classList.contains('current-menu-parent'),
    )
    .at(0);

  if (!(activeMenuItem instanceof HTMLElement)) return null;

  return activeMenuItem && getComputedStyle(activeMenuItem).display !== 'none'
    ? activeMenuItem
    : null;
};

const moveToMenuItem = (menuItemElement: HTMLElement) => {
  if (!underlineElement) return;

  const { navbarNavElement: nav, link, icon } = getElements(menuItemElement);
  if (!nav || !link) return;

  const navRect = nav.getBoundingClientRect();
  const linkRect = link.getBoundingClientRect();

  const iconSpace = icon
    ? icon.getBoundingClientRect().width +
      (parseFloat(getComputedStyle(icon).marginLeft) || 0) +
      (parseFloat(getComputedStyle(icon).marginRight) || 0)
    : 0;

  const visualWidth = Math.max(0, linkRect.width - iconSpace);
  const centerX = linkRect.left + visualWidth / 2;

  const underlineWidth = 16;
  const x = centerX - navRect.left - underlineWidth / 2;

  if (!getActiveMenuItem() && !hasDoneFirstMove) {
    underlineElement.classList.add('underline--no-transitions');
    nextTick(() =>
      underlineElement!.classList.remove('underline--no-transitions'),
    );
    hasDoneFirstMove = true;
  }

  underlineElement.classList.remove('underline--hidden');
  Object.assign(underlineElement.style, {
    width: `${underlineWidth}px`,
    transform: `translateX(${x}px)`,
  });
};

const moveToActiveMenuItem = (skipTransitions?: boolean) => {
  if (!underlineElement) return;
  const activeMenuItem = getActiveMenuItem();

  if (!activeMenuItem) {
    underlineElement.classList.add('underline--hidden');
    return;
  }

  if (skipTransitions) {
    underlineElement.classList.add('underline--no-transitions');
    nextTick(() =>
      underlineElement!.classList.remove('underline--no-transitions'),
    );
  }

  moveToMenuItem(activeMenuItem);
};

const breakpointMediaChecker = () => {
  if (!underlineElement) return;

  if (!getActiveMenuItem()) {
    underlineElement.classList.add('underline--hidden');
    return;
  }

  nextTick(() => moveToActiveMenuItem(true));
  underlineElement.classList.toggle(
    'underline--hidden',
    !BREAKPOINT_MEDIA_QUERIES.lg.matches,
  );
};

export default defineModule(
  () => {
    const { navbarElement, navbarNavElement } = getElements();
    if (!navbarElement || !navbarNavElement) return;

    underlineElement = createElement(
      'div',
      {
        className:
          'underline--hidden hidden lg:grid absolute place-items-center bottom-2 [&:not(.underline--no-transitions)]:transition-[transform,width,opacity,visibility] [&:not(.underline--no-transitions)]:duration-300 [&:not(.underline--no-transitions)]:ease-in-out [&.underline--hidden]:invisible [&.underline--hidden]:opacity-0 pointer-events-none',
      },
      [
        createElement('div', {
          className:
            'h-1 w-4 bg-black dark:bg-white rounded-full transition-colors duration-300 ease-in-out',
        }),
      ],
    );
    navbarNavElement.appendChild(underlineElement);

    moveToActiveMenuItem(true);

    nextTick(breakpointMediaChecker);

    BREAKPOINT_MEDIA_QUERIES.lg.addEventListener(
      'change',
      breakpointMediaChecker,
    );

    const menuItemElements = [...navbarNavElement.children];
    menuItemElements.forEach((menuItemElement) => {
      if (!(menuItemElement instanceof HTMLElement)) return;

      menuItemElement.addEventListener('mouseenter', () =>
        moveToMenuItem(menuItemElement),
      );

      menuItemElement.addEventListener('mouseleave', () =>
        moveToActiveMenuItem(),
      );
    });
  },
  () => {
    hasDoneFirstMove = false;

    const { navbarElement, navbarNavElement } = getElements();
    if (!navbarElement || !navbarNavElement || !underlineElement) return;

    underlineElement.remove();

    BREAKPOINT_MEDIA_QUERIES.lg.removeEventListener(
      'change',
      breakpointMediaChecker,
    );

    const menuItemElements = [...navbarNavElement.children];
    menuItemElements.forEach((menuItemElement) => {
      if (!(menuItemElement instanceof HTMLElement)) return;

      menuItemElement.removeEventListener('mouseenter', () =>
        moveToMenuItem(menuItemElement),
      );

      menuItemElement.removeEventListener('mouseleave', () =>
        moveToActiveMenuItem(),
      );
    });
  },
);
